/**
 * JSON Dialog Renderer
 * Renders dialog data as structured JSON with sections, navigation, conditions, and options
 */

import { NamingConvention } from '../../../../types/exportTemplates';
import type { StringsItem } from '../../../stores/stringsStore';
import { applyNamingConvention } from '../../core/naming';
import { stripHtmlTags } from '../../core/formatting';

/**
 * Helper: resolve a string_id to its translations object { lang: text }
 */
function resolveTranslations(
  stringId: string | null | undefined,
  stringsMap: Map<string, StringsItem>
): Record<string, string> | null {
  if (!stringId) return null;
  const stringItem = stringsMap.get(stringId);
  if (!stringItem) return null;

  const translations: Record<string, string> = {};

  // Source text as fallback for source language
  if (stringItem.source_text) {
    translations['source'] = stripHtmlTags(stringItem.source_text);
  }

  if (stringItem.translations && Array.isArray(stringItem.translations)) {
    stringItem.translations.forEach(trans => {
      if (trans.language_code && trans.text) {
        translations[trans.language_code] = stripHtmlTags(trans.text);
      }
    });
  }

  return Object.keys(translations).length > 0 ? translations : null;
}

/**
 * Render a single dialog option (for question nodes) to a JSON-serializable object
 */
function renderOption(option: any, stringsMap: Map<string, StringsItem>): Record<string, any> {
  const opt: Record<string, any> = {};
  
  // Text with translations
  const optTranslation = resolveTranslations(option.string_id, stringsMap);
  opt.text = optTranslation || (option.option_text ? stripHtmlTags(option.option_text) : '');

  // Navigation (prefer new properties, fall back to legacy)
  const nextNode = option.next_node || option.next_section || null;
  if (nextNode) opt.next_node = nextNode;
  
  if (option.next_dialog) {
    opt.next_dialog = option.next_dialog;
    const nextDialogNode = option.next_dialog_node || option.next_dialog_section || null;
    if (nextDialogNode) opt.next_dialog_node = nextDialogNode;
  }

  // Variable action (set/increment/decrement when this option is selected)
  if (option.variableAction) {
    opt.variable_action = {
      variable: option.variableAction.variable,
      operator: option.variableAction.operator,
      value: option.variableAction.value
    };
  }

  // Visibility condition (show option only if condition is met)
  if (option.visibilityCondition) {
    opt.visibility_condition = {
      variable: option.visibilityCondition.variable,
      operator: option.visibilityCondition.operator,
      value: option.visibilityCondition.value
    };
  }

  // Generic conditions array (if present)
  if (option.conditions && Array.isArray(option.conditions) && option.conditions.length > 0) {
    opt.conditions = option.conditions;
  }

  // Generic actions array (if present)
  if (option.actions && Array.isArray(option.actions) && option.actions.length > 0) {
    opt.actions = option.actions;
  }

  return opt;
}

/**
 * Render a single condition option to a JSON-serializable object
 */
function renderConditionOption(condOption: any): Record<string, any> {
  const opt: Record<string, any> = {};

  if (condOption.operator) opt.operator = condOption.operator;
  if (condOption.condition_value !== undefined && condOption.condition_value !== null) {
    opt.value = condOption.condition_value;
  }
  // Also support "value" directly (from legacy or alternative structures)
  if (opt.value === undefined && condOption.value !== undefined && condOption.value !== null) {
    opt.value = condOption.value;
  }

  // Navigation (prefer new properties, fall back to legacy)
  const nextNode = condOption.next_node || condOption.next_section || null;
  if (nextNode) opt.next_node = nextNode;

  if (condOption.next_dialog) {
    opt.next_dialog = condOption.next_dialog;
    const nextDialogNode = condOption.next_dialog_node || condOption.next_dialog_section || null;
    if (nextDialogNode) opt.next_dialog_node = nextDialogNode;
  }

  if (condOption.order_index !== undefined) opt.order_index = condOption.order_index;

  return opt;
}

/**
 * Render dialogs as a JSON string
 *
 * @param dialogs - Array of dialog objects
 * @param folderPathResolver - Function to resolve folder IDs to paths
 * @param stringsMap - Map of string IDs to StringsItem for translations
 * @param namingConvention - Naming convention to apply
 */
export function renderJsonDialogsFile(
  dialogs: any[],
  folderPathResolver: (folderId: string | null | undefined) => string,
  stringsMap: Map<string, StringsItem>,
  namingConvention: NamingConvention
): string {
  if (!dialogs || dialogs.length === 0) {
    return JSON.stringify({ dialogs: {} }, null, 2);
  }

  const dialogsOutput: Record<string, any> = {};

  dialogs.forEach(dialog => {
    const dialogKey = applyNamingConvention(
      dialog.dialog_key || dialog.name || 'unknown',
      namingConvention
    );

    const folderPath = dialog.folder ? folderPathResolver(dialog.folder) : '';

    const dialogObj: Record<string, any> = {
      id: dialog.id,
      name: dialog.name || '',
      dialog_key: dialog.dialog_key || dialog.name || '',
      folder: folderPath || null,
      status: dialog.status || 'draft',
      starting_node: dialog.data?.startingNode || dialog.data?.startingSection || null,
      nodes: [] as any[]
    };

    // Variables declared on this dialog
    if (dialog.data?.variables && Array.isArray(dialog.data.variables) && dialog.data.variables.length > 0) {
      dialogObj.variables = dialog.data.variables.map((variable: any) => {
        const v: Record<string, any> = {
          name: variable.name,
          type: variable.type || 'boolean',
          initial_value: variable.initialValue ?? (variable.type === 'number' ? 0 : false),
        };
        if (variable.description) v.description = variable.description;
        if (variable.rangeMin !== undefined) v.range_min = variable.rangeMin;
        if (variable.rangeMax !== undefined) v.range_max = variable.rangeMax;
        if (variable.increment !== undefined) v.increment = variable.increment;
        return v;
      });
    }

    // Process nodes (new structure) or sections (legacy)
    if (dialog.data?.nodes && Array.isArray(dialog.data.nodes)) {
      dialogObj.nodes = dialog.data.nodes.map((node: any) => {
        const section: Record<string, any> = {
          id: node.id || null,
          type: node.type || 'dialog',
          node_identifier: node.node_identifier || node.section_identifier || null,
          order_index: node.order_index ?? 0
        };

        if (node.name) section.name = node.name;

        // Text / dialog text
        if (node.text) {
          const translationObj = resolveTranslations(node.string_id, stringsMap);
          section.text = translationObj || stripHtmlTags(node.text);
        }

        // Speaker
        if (node.speaker) {
          section.speaker = node.speaker;
        }

        // Navigation (prefer new properties, fall back to legacy)
        const nextNode = node.next_node || node.next_section || null;
        if (nextNode) section.next_node = nextNode;

        if (node.next_dialog) {
          section.next_dialog = node.next_dialog;
          const nextDialogNode = node.next_dialog_node || node.next_dialog_section || null;
          if (nextDialogNode) section.next_dialog_node = nextDialogNode;
        }

        // =====================================================
        // CONDITION NODE (type: "condition")
        // =====================================================
        if (node.type === 'condition') {
          // Primary condition config: which variable is being checked
          if (node.condition) {
            section.condition = {
              variable: node.condition.variable || null
            };
            if (node.condition.checkVariable) {
              section.condition.check_variable = node.condition.checkVariable;
            }
          }

          // Condition options: each branch with operator, value, and navigation target
          if (node.conditionOptions && Array.isArray(node.conditionOptions) && node.conditionOptions.length > 0) {
            section.condition_options = node.conditionOptions.map(renderConditionOption);
          }

          // Legacy true/false branching (backwards compatibility)
          if (node.next_section_true || node.next_dialog_true) {
            section.legacy_true_branch = {};
            if (node.next_section_true) section.legacy_true_branch.next_node = node.next_section_true;
            if (node.next_dialog_true) {
              section.legacy_true_branch.next_dialog = node.next_dialog_true;
              if (node.next_dialog_section_true) section.legacy_true_branch.next_dialog_node = node.next_dialog_section_true;
            }
          }
          if (node.next_section_false || node.next_dialog_false) {
            section.legacy_false_branch = {};
            if (node.next_section_false) section.legacy_false_branch.next_node = node.next_section_false;
            if (node.next_dialog_false) {
              section.legacy_false_branch.next_dialog = node.next_dialog_false;
              if (node.next_dialog_section_false) section.legacy_false_branch.next_dialog_node = node.next_dialog_section_false;
            }
          }
        }

        // =====================================================
        // SETTER NODE (type: "setter")
        // =====================================================
        if (node.type === 'setter' && node.setter) {
          section.setter = {
            variable: node.setter.variable,
            operator: node.setter.operator,
            value: node.setter.value
          };
        }

        // =====================================================
        // QUESTION NODE (type: "question")
        // =====================================================
        if (node.options && Array.isArray(node.options) && node.options.length > 0) {
          section.options = node.options.map((option: any) => renderOption(option, stringsMap));
        }

        // Generic conditions array (if present on any node)
        if (node.conditions && Array.isArray(node.conditions) && node.conditions.length > 0) {
          section.conditions = node.conditions;
        }

        // Generic actions array (if present on any node)
        if (node.actions && Array.isArray(node.actions) && node.actions.length > 0) {
          section.actions = node.actions;
        }

        // Context note
        if (node.context) {
          section.context = node.context;
        }

        return section;
      });
    } else if (dialog.data?.sections && Array.isArray(dialog.data.sections)) {
      // Legacy sections format
      dialogObj.nodes = dialog.data.sections.map((section: any) => {
        const sectionObj: Record<string, any> = {
          node_identifier: section.section_identifier || section.name || '',
          name: section.name || null,
          fields: [] as any[]
        };

        if (section.fields && Array.isArray(section.fields)) {
          sectionObj.fields = section.fields.map((field: any) => {
            const fieldObj: Record<string, any> = {
              type: field.type || 'dialog'
            };

            if (field.text) {
              const translationObj = resolveTranslations(field.string_id, stringsMap);
              fieldObj.text = translationObj || stripHtmlTags(field.text);
            }

            if (field.speaker) fieldObj.speaker = field.speaker;

            // Navigation
            const nextNode = field.next_node || field.next_section || null;
            if (nextNode) fieldObj.next_node = nextNode;
            if (field.next_dialog) {
              fieldObj.next_dialog = field.next_dialog;
              const nextDialogNode = field.next_dialog_node || field.next_dialog_section || null;
              if (nextDialogNode) fieldObj.next_dialog_node = nextDialogNode;
            }

            // Condition data (for condition fields)
            if (field.type === 'condition') {
              if (field.condition) {
                fieldObj.condition = {
                  variable: field.condition.variable || null
                };
                if (field.condition.checkVariable) {
                  fieldObj.condition.check_variable = field.condition.checkVariable;
                }
              }
              if (field.conditionOptions && Array.isArray(field.conditionOptions) && field.conditionOptions.length > 0) {
                fieldObj.condition_options = field.conditionOptions.map(renderConditionOption);
              }
            }

            // Setter data
            if (field.type === 'setter' && field.setter) {
              fieldObj.setter = {
                variable: field.setter.variable,
                operator: field.setter.operator,
                value: field.setter.value
              };
            }

            // Options (for question fields)
            if (field.options && Array.isArray(field.options) && field.options.length > 0) {
              fieldObj.options = field.options.map((option: any) => renderOption(option, stringsMap));
            }

            return fieldObj;
          });
        }

        return sectionObj;
      });
    }

    dialogsOutput[dialogKey] = dialogObj;
  });

  console.log(`✅ Rendered ${dialogs.length} dialogs as JSON`);

  return JSON.stringify({ dialogs: dialogsOutput }, null, 2);
}
